import React, { useEffect, useState, useCallback } from "react";
import axios from "../../axios/axios";
import { AiOutlineDelete } from "react-icons/ai";
import { Link } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import Layout from "./Layout";
import Navbar from "../../Components/Navbar";
import Footer from "../../Components/Footer";
import { ToastContainer, toast } from "react-toastify";
import PaginationControls from "../../Components/PaginationControls";
import RowsPerPageSelect from "../../Components/RowsPerPageSelect";
import ExportCsvButton from "../../Components/ExportCsvButton";
import DateRangeFilter from "../../Components/DateRangeFilter";

const UserTable = () => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loadingUserId, setLoadingUserId] = useState(null); // Track which user's button is loading
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const getUsers = useCallback(async () => {
    try {
      const response = await axios.get("/user/allUsers", {
        params: {
          page: currentPage,
          limit: rowsPerPage,
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      });
      const filteredUsers = response.data.results.filter(
        (user) => user.role !== "admin"
      );
      setUsers(filteredUsers);
      setTotalPages(response.data.totalPages);
      setTotalResults(response.data.totalResults);
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    }
  }, [currentPage, rowsPerPage, startDate, endDate]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const deleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await axios.delete(`/user/${id}`);
      toast.success("User deleted successfully!");
      getUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };

  const toggleUserStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "pending" ? "active" : "pending";
    setLoadingUserId(id); // Start loading for this user

    try {
      await axios.put("/user/updateUserStatus", {
        userId: id,
        status: newStatus,
      });
      toast.success(`User is now ${newStatus}`);
      getUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update status");
    } finally {
      setLoadingUserId(null); // Stop loading
    }
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const response = await axios.get("/user/allUsers", {
      params: {
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      },
    });
    return response.data.filter((u) => u.role !== "admin");
  }, [startDate, endDate]);

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const csvColumns = [
    { label: "Name", accessor: (row) => row.fname },
    { label: "User ID", accessor: (row) => row.id },
    { label: "Email", accessor: (row) => row.email },
    { label: "Role", accessor: (row) => row.role },
    {
      label: "Date Created",
      accessor: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
    { label: "Status", accessor: (row) => row.status },
  ];

  return (
    <>
      <Navbar />
      <Layout>
        <div className="container-xl bg-light">
          <ToastContainer
            position="top-right"
            autoClose={5000}
            theme="colored"
          />
          <div className="table-responsive bg-light">
            <div className="table-wrapper bg-light">
              <h2 className="text-center mb-5">User Management</h2>
              <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-2">
                <DateRangeFilter
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={handleStartDateChange}
                  onEndDateChange={handleEndDateChange}
                  onClear={handleClearDates}
                />
                <div className="d-flex align-items-center gap-3">
                  <ExportCsvButton
                    filename="users.csv"
                    columns={csvColumns}
                    fetchRows={fetchAllForExport}
                  />
                  <RowsPerPageSelect
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </div>
              </div>
              <table className="table table-hover table-light">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>User ID</th>
                    <th>Date Created</th>
                    <th>User Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user.id}>
                      <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                      <td>{user.fname}</td>
                      <td>{user.id}</td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button
                          onClick={() => toggleUserStatus(user.id, user.status)}
                          className={`btn rounded px-3 py-2 ${
                            user.status === "pending"
                              ? "bg-warning text-light"
                              : "bg-success text-light"
                          }`}
                          disabled={loadingUserId === user.id} // Disable button if it's loading
                        >
                          {loadingUserId === user.id
                            ? "Loading..."
                            : user.status.charAt(0).toUpperCase() +
                              user.status.slice(1)}
                        </button>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Link
                            to={`/user-detail-admin/${user.id}`}
                            className="d-flex align-items-center gap-2 text_maincolor"
                          >
                            <FaEye />
                          </Link>
                          <a href="##" onClick={() => deleteUser(user.id)}>
                            <AiOutlineDelete className="text-danger" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                totalResults={totalResults}
                onPageChange={(page) =>
                  setCurrentPage(Math.min(Math.max(page, 1), totalPages || 1))
                }
              />
            </div>
          </div>
        </div>
      </Layout>
      <Footer />
    </>
  );
};

export default UserTable;
